import { ResolveFn, Router } from '@angular/router';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { RfpContractShowResponse } from '../models/rfp-contract';
import { RfpContractService } from '../services/rfp-contract/rfp-contract.service';
import { NavigationPipe } from '../pipes/navigation/navigation.pipe';
import { Path } from '../constants/paths.constant';

/**
 * A failed fetch (404 — ownership, or a contractor hitting a still-`draft`
 * contract, per spec §36) doesn't throw here: `HttpBaseService` swallows the
 * error into a successful emission by default, so the response comes back
 * without a `contract` field rather than as a caught error. Detect that and
 * redirect to the list, the same defensive pattern `quotationRFPDetailsResolver`
 * already uses for the same reason.
 */
export const rfpContractDetailsResolver: ResolveFn<RfpContractShowResponse> = (
  route,
  state
) => {
  const rfpContractService = inject(RfpContractService);
  const destroyRef = inject(DestroyRef);
  const router = inject(Router);
  const navigation = new NavigationPipe();

  const contractId = route.params['contractId'];
  const isBuyer = state.url.includes('buyer');

  const request = isBuyer
    ? rfpContractService.getBuyerContract(contractId)
    : rfpContractService.getContractorContract(contractId);

  return request.pipe(
    takeUntilDestroyed(destroyRef),
    tap(response => {
      if (!response?.contract) {
        router.navigate([
          navigation.transform(
            isBuyer
              ? `${Path.BUYER}/${Path.CONTRACT.CONTRACT}`
              : `${Path.CONTRACTOR}/${Path.CONTRACT.CONTRACT}`
          ),
        ]);
      }
    })
  );
};
